import {
  AddIcon,
  AiPanel,
  Button,
  Divider,
  Stack,
  Tag,
  Typography,
} from '@all-manager/design-system-v2';
import { useTopicRecommendationSection } from './useTopicRecommendationSection';
import type { TopicRecommendation } from '../../types/inbox.types';

type Props = {
  recommendation?: TopicRecommendation | null;
  currentSubTopicId: string | null;
  onSelectSubTopic: (subTopicId: string) => void;
  onCreateNewTopicClick: () => void;
};

const DEFAULT_NEW_TOPIC_REASON =
  "L'IA suggère de créer un sujet dédié car aucun dossier actif existant ne semble correspondre.";

export default function TopicRecommendationSection(props: Props) {
  const hook = useTopicRecommendationSection(props);

  if (!hook.hasRecommendation) {
    return null;
  }

  return (
    <AiPanel title="Recommandation de sujet">
      {hook.explanation && (
        <Stack spacing={0.5}>
          <Typography variant="body1Emphasis">Pourquoi ce sujet ?</Typography>
          <Typography variant="body2">{hook.explanation}</Typography>
        </Stack>
      )}

      {hook.hasHesitations && (
        <>
          {hook.explanation && <Divider />}
          <Stack spacing={1}>
            <Typography variant="body1Emphasis">Autres sujets envisagés</Typography>
            {hook.hesitations.map(function renderHesitation(item, index) {
              const isSelected = Boolean(item.subTopicId && props.currentSubTopicId === item.subTopicId);

              return (
                <Stack
                  key={item.subTopicId ?? index}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  justifyContent="space-between"
                >
                  <Stack spacing={0.5}>
                    <Typography variant="body1Emphasis">{item.subTopicName}</Typography>
                    {item.reason && (
                      <Typography variant="body2">
                        {item.reason}
                      </Typography>
                    )}
                  </Stack>
                  {isSelected ? (
                    <Tag color="done" variantIcon="state" label="Sélectionné" />
                  ) : (
                    item.subTopicId && (
                      <Button
                        variant="text"
                        size="small"
                        color="default"
                        onClick={function handlePick() {
                          hook.handleSelectHesitation(item.subTopicId);
                        }}
                      >
                        Basculer sur ce sujet
                      </Button>
                    )
                  )}
                </Stack>
              );
            })}
          </Stack>
        </>
      )}

      {hook.shouldCreateNewTopic && (
        <>
          {(hook.explanation || hook.hasHesitations) && <Divider />}
          <Stack spacing={1} alignItems="flex-start">
            <Typography variant="body1Emphasis">Nouveau sujet conseillé</Typography>
            <Typography variant="body2">{hook.newTopicReason || DEFAULT_NEW_TOPIC_REASON}</Typography>
            {hook.suggestedNewTopicTitle && (
              <Typography variant="body2">
                Titre suggéré : « {hook.suggestedNewTopicTitle} »
              </Typography>
            )}
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={props.onCreateNewTopicClick}>
              Créer ce sujet
            </Button>
          </Stack>
        </>
      )}
    </AiPanel>
  );
}
